// CELL-BIO9 — single source of truth for game state.
// Stats: rep / intel / muscle / heat, clamped 0–100. Day counter. Flag set.

import type { Effects, Requirements, StatKey } from "./types";

export interface StateSnapshot {
  stats: Record<StatKey, number>;
  day: number;
  flags: string[];
  currentNode: string;
}

const STAT_MIN = 0;
const STAT_MAX = 100;

function clamp(value: number): number {
  return Math.max(STAT_MIN, Math.min(STAT_MAX, Math.round(value)));
}

type Listener = (state: GameState) => void;

export class GameState {
  rep = 10;
  intel = 10;
  muscle = 10;
  heat = 0;
  day = 1;
  flags: Set<string> = new Set();
  currentNode = "";

  private listeners: Listener[] = [];

  onChange(listener: Listener): void {
    this.listeners.push(listener);
  }

  private emit(): void {
    for (const listener of this.listeners) listener(this);
  }

  /** Apply bounded stat effects. All values are clamped into [0, 100]. */
  applyEffects(effects: Effects | undefined): void {
    if (!effects) return;
    for (const key of ["rep", "intel", "muscle", "heat"] as const) {
      const delta = effects[key];
      if (typeof delta === "number" && delta !== 0) {
        this[key] = clamp(this[key] + delta);
      }
    }
    this.emit();
  }

  addFlags(flags: string[] | undefined): void {
    if (!flags || flags.length === 0) return;
    for (const flag of flags) this.flags.add(flag);
    this.emit();
  }

  removeFlags(flags: string[] | undefined): void {
    if (!flags || flags.length === 0) return;
    for (const flag of flags) this.flags.delete(flag);
    this.emit();
  }

  advanceDay(): void {
    this.day += 1;
    this.emit();
  }

  setCurrentNode(id: string): void {
    this.currentNode = id;
    this.emit();
  }

  /** Requirements gating for choices. */
  meets(requires: Requirements | undefined): boolean {
    if (!requires) return true;
    if (requires.intel_min !== undefined && this.intel < requires.intel_min) return false;
    if (requires.rep_min !== undefined && this.rep < requires.rep_min) return false;
    if (requires.muscle_min !== undefined && this.muscle < requires.muscle_min) return false;
    if (requires.heat_max !== undefined && this.heat > requires.heat_max) return false;
    if (requires.flags && !requires.flags.every((f) => this.flags.has(f))) return false;
    if (requires.not_flags && requires.not_flags.some((f) => this.flags.has(f))) return false;
    return true;
  }

  snapshot(): StateSnapshot {
    return {
      stats: { rep: this.rep, intel: this.intel, muscle: this.muscle, heat: this.heat },
      day: this.day,
      flags: [...this.flags],
      currentNode: this.currentNode,
    };
  }

  restore(snap: StateSnapshot): void {
    this.rep = clamp(snap.stats.rep);
    this.intel = clamp(snap.stats.intel);
    this.muscle = clamp(snap.stats.muscle);
    this.heat = clamp(snap.stats.heat);
    this.day = Math.max(1, Math.round(snap.day));
    this.flags = new Set(snap.flags);
    this.currentNode = snap.currentNode;
    this.emit();
  }
}

export const gameState = new GameState();